import { useCallback, useEffect, useRef } from 'react';
import {
  lockAxis,
  classifySwipe,
  followOffset,
  adjacentTabId,
  findHorizontalScroller,
  scrollEdgeState,
  scrollerBlocksSwipe,
  isSwipeExcludedTarget,
  prefersReducedMotion,
} from '../utils/swipeTabs';

/**
 * `useSwipeTabs(areaRef, { tabs, active, onChange, enabled })` → touch handlers
 * to spread on the swipe AREA (the list under a tab strip). THE "swipe the list
 * to change tab" behaviour, added 2026-09-30 for My Orders on a phone. Every
 * rule it applies is a pure function in `utils/swipeTabs.js` — read that
 * docblock for the rules themselves; this file is only the wiring.
 *
 * 🔑 **`onChange` is the caller's own tab handler, unchanged** — on My Orders
 * that is `continueWhenClean(() => setActiveTab(id))`, so a swipe over a dirty
 * inline edit ASKS first, exactly like a tap on the strip. A swipe is a second
 * way to press a tab, never a second tab state.
 *
 * 🎞️ **The list is moved through `areaRef.current.style`, never React state** —
 * a re-render of fifty rows per `touchmove` is the jank this would otherwise
 * be. 🚨 **And the transform is CLEARED (set to `''`, not `translateX(0)`) the
 * moment it is not animating**: a transform makes the element a stacking
 * context and a containing block, which would trap the `ui/Toolbar` z-index
 * contract's dropdowns and any `fixed` child inside the list.
 *
 * ♿ Reduced motion: no follow and no slide — the tab simply switches.
 * ⚠️ Listeners are React's (passive for touch in React 17+), and nothing calls
 * `preventDefault`, so the browser's own vertical scroll is never held up.
 * jsdom proves the decisions; how it FEELS is a browser check.
 */
export const SWIPE_SNAP_BACK_MS = 180;
export const SWIPE_SLIDE_IN_MS = 200;
export const SWIPE_SLIDE_IN_PX = 48;

function clearStyle(el) {
  if (!el) return;
  el.style.transform = '';
  el.style.transition = '';
  el.style.opacity = '';
}

export default function useSwipeTabs(areaRef, { tabs, active, onChange, enabled = true }) {
  const g = useRef(null);
  const pendingDir = useRef(null);
  const timer = useRef(null);
  const latest = useRef({ tabs, active, onChange });
  latest.current = { tabs, active, onChange };

  const later = (fn, ms) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; fn(); }, ms);
  };

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const snapBack = useCallback(() => {
    const el = areaRef.current;
    if (!el || !el.style.transform) { clearStyle(el); return; }
    el.style.transition = `transform ${SWIPE_SNAP_BACK_MS}ms ease-out`;
    el.style.transform = 'translateX(0px)';
    later(() => clearStyle(areaRef.current), SWIPE_SNAP_BACK_MS);
  }, [areaRef]);

  /* The slide-in plays when the tab ACTUALLY changes — not at commit — so a
     swipe the dirty-edit prompt holds (or that the user answers "Keep
     editing" to) never animates content that did not change. */
  useEffect(() => {
    const dir = pendingDir.current;
    pendingDir.current = null;
    const el = areaRef.current;
    if (!dir || !el || prefersReducedMotion()) return;
    el.style.transition = 'none';
    el.style.opacity = '0.4';
    el.style.transform = `translateX(${dir === 'next' ? SWIPE_SLIDE_IN_PX : -SWIPE_SLIDE_IN_PX}px)`;
    // Force the start frame to paint before transitioning away from it.
    void el.offsetWidth; // eslint-disable-line no-void
    el.style.transition = `transform ${SWIPE_SLIDE_IN_MS}ms ease-out, opacity ${SWIPE_SLIDE_IN_MS}ms ease-out`;
    el.style.transform = 'translateX(0px)';
    el.style.opacity = '1';
    later(() => clearStyle(areaRef.current), SWIPE_SLIDE_IN_MS);
  }, [active, areaRef]);

  const onTouchStart = useCallback((e) => {
    if (!enabled || !e.touches || e.touches.length !== 1) { g.current = null; return; }
    const t = e.touches[0];
    const scroller = findHorizontalScroller(e.target, areaRef.current);
    g.current = {
      x: t.clientX,
      y: t.clientY,
      t: Date.now(),
      axis: null,
      dx: 0,
      dy: 0,
      excluded: isSwipeExcludedTarget(e.target),
      edge: scroller ? scrollEdgeState(scroller) : null,
      reduced: prefersReducedMotion(),
    };
  }, [enabled, areaRef]);

  const onTouchMove = useCallback((e) => {
    const s = g.current;
    if (!s || s.excluded || !e.touches || !e.touches[0]) return;
    const t = e.touches[0];
    s.dx = t.clientX - s.x;
    s.dy = t.clientY - s.y;
    if (s.axis === null) {
      s.axis = lockAxis(s.dx, s.dy);
      if (s.axis === 'x' && scrollerBlocksSwipe(s.dx < 0 ? 'next' : 'prev', s.edge)) {
        // The table owns this one — it is still scrolling toward that edge.
        s.axis = 'owned';
      }
    }
    if (s.axis !== 'x' || s.reduced) return;
    const el = areaRef.current;
    if (!el) return;
    // No neighbour that way → no follow either: the list must not promise a move.
    const { tabs: ts, active: a } = latest.current;
    const dir = s.dx < 0 ? 'next' : 'prev';
    if (!adjacentTabId(ts, a, dir)) { if (el.style.transform) snapBack(); return; }
    el.style.transition = 'none';
    el.style.transform = `translateX(${followOffset(s.dx)}px)`;
  }, [areaRef, snapBack]);

  const finish = useCallback((e, cancelled) => {
    const s = g.current;
    g.current = null;
    if (!s || s.excluded || s.axis !== 'x' || cancelled) { snapBack(); return; }
    const t = e && e.changedTouches && e.changedTouches[0];
    if (t) { s.dx = t.clientX - s.x; s.dy = t.clientY - s.y; }
    const dir = classifySwipe({ dx: s.dx, dy: s.dy, dtMs: Date.now() - s.t });
    const { tabs: ts, active: a, onChange: change } = latest.current;
    const next = dir ? adjacentTabId(ts, a, dir) : null;
    if (!next) { snapBack(); return; }
    // Hand the list back to rest BEFORE the switch, so a held (dirty-edit)
    // change leaves nothing half-translated behind the prompt.
    clearStyle(areaRef.current);
    pendingDir.current = s.reduced ? null : dir;
    // A change the dirty-edit prompt HOLDS may never land; do not let an
    // unrelated later tap inherit this swipe's slide.
    const armed = pendingDir.current;
    setTimeout(() => { if (pendingDir.current === armed) pendingDir.current = null; }, 400);
    if (change) change(next);
  }, [areaRef, snapBack]);

  const onTouchEnd = useCallback((e) => finish(e, false), [finish]);
  const onTouchCancel = useCallback((e) => finish(e, true), [finish]);

  if (!enabled) return {};
  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel };
}
